/**
 * Typed-answer evaluation vocabulary.
 *
 * The evaluation engine decides only *how good an answer is*. It never computes
 * points and never sees a question's cell value — the scoring engine consumes
 * the finalized opinion separately, so grading rules and scoring rules can move
 * independently.
 *
 * The four opinions are exhaustive and Unknown is a real outcome, not a failure:
 * an engine that manufactures a correct/incorrect judgement out of genuine
 * ambiguity teaches the Answer Map a wrong fact, and a wrong fact in the trusted
 * map is worse than a facilitator spending three seconds on a ruling.
 */

import type { Id } from "./types.js";

// ─── Opinions ────────────────────────────────────────────────────────────────

/** The four opinions the evaluator may return. */
export const ANSWER_OPINIONS = ["correct", "partial", "incorrect", "unknown"] as const;
export type AnswerOpinion = (typeof ANSWER_OPINIONS)[number];

/**
 * The three opinions an Answer Map may *store*. Unknown is a temporary
 * unresolved state — it means "nobody has decided yet", which is not a fact
 * about the answer and therefore not something to remember.
 */
export const RESOLVED_OPINIONS = ["correct", "partial", "incorrect"] as const;
export type ResolvedOpinion = (typeof RESOLVED_OPINIONS)[number];

export function isResolvedOpinion(value: unknown): value is ResolvedOpinion {
  return value === "correct" || value === "partial" || value === "incorrect";
}

export function isAnswerOpinion(value: unknown): value is AnswerOpinion {
  return isResolvedOpinion(value) || value === "unknown";
}

// ─── Provenance ──────────────────────────────────────────────────────────────

/**
 * Where an opinion came from. Provenance is kept forever so a machine-learned
 * judgement can be reviewed, corrected, or audited later, and so the frequency
 * with which a source gets overridden is measurable.
 */
export const OPINION_SOURCES = [
  /** Supplied by the question author: canonical answer, accepted answers, distractors. */
  "author",
  /** Ruled on by a facilitator or an authorized reviewer. */
  "human",
  /** Produced by semantic evaluation. */
  "ai",
  /** Inherited from a known answer through a conservative fuzzy match. */
  "fuzzy",
  /** Pre-generated during Answer Map enrichment, never yet seen from a player. */
  "generated",
] as const;
export type OpinionSource = (typeof OPINION_SOURCES)[number];

/** How a particular evaluation was reached, independent of the opinion's provenance. */
export const EVALUATION_METHODS = [
  /** Exact hit on a normalized Answer Map key. */
  "exact",
  /** Conservative fuzzy match onto a known answer. */
  "fuzzy",
  /** Semantic evaluation. */
  "ai",
  /** A facilitator ruling. */
  "human",
  /** Nothing could decide it; the opinion is Unknown. */
  "unresolved",
] as const;
export type EvaluationMethod = (typeof EVALUATION_METHODS)[number];

/**
 * Which map an entry lives in. A session entry is immediately reusable for the
 * rest of the current game; a trusted entry is permanent knowledge carried into
 * every future game that uses the question.
 */
export const ANSWER_MAP_SCOPES = ["session", "trusted"] as const;
export type AnswerMapScope = (typeof ANSWER_MAP_SCOPES)[number];

// ─── The evaluation result (§15) ─────────────────────────────────────────────

/**
 * The evaluator's standardized output. Deliberately contains no points, no
 * multiplier, and no cell value.
 */
export interface AnswerEvaluation {
  opinion: AnswerOpinion;
  /** 0..1. For an exact hit on a human ruling this is 1. */
  confidence: number;
  /** Provenance of the opinion — null only when the opinion is Unknown. */
  source: OpinionSource | null;
  /** How this particular evaluation was reached. */
  method: EvaluationMethod;
  /** Short human-readable justification, shown to a facilitator during review. */
  reason: string | null;
  matchedConcepts: string[];
  missingConcepts: string[];
  /** Concepts stated by the player that contradict the rubric. */
  contradictoryConcepts: string[];
  /** The known answer a fuzzy match rode in on, for auditability. */
  matchedAnswer: string | null;
  /** The similarity that justified a fuzzy match, when one was used. */
  fuzzySimilarity: number | null;
}

// ─── Answer Map (§1, §17) ────────────────────────────────────────────────────

/**
 * One learned way of answering one specific question.
 *
 * Entries are question-scoped by construction: a judgement about "diversification"
 * as an answer to one question says nothing about it as an answer to another.
 */
export interface AnswerMapEntry {
  id: Id;
  tenantId: Id;
  questionId: Id;
  /** The Answer Map key. */
  normalizedAnswer: string;
  /** Original spellings players actually typed, capped and oldest-first. */
  examples: string[];
  opinion: ResolvedOpinion;
  source: OpinionSource;
  /** Null when the source carries no meaningful confidence (author, human). */
  confidence: number | null;
  reason: string | null;
  /** Set when the entry was created by a fuzzy match. */
  matchedAnswer: string | null;
  fuzzySimilarity: number | null;
  timesSeen: number;
  firstSeenAt: Date;
  lastSeenAt: Date;
  humanReviewed: boolean;
  /** How often this entry's judgement has been changed after the fact. */
  overrideCount: number;
  scope: AnswerMapScope;
}

/** One recorded change to an Answer Map entry's judgement (§13). */
export interface AnswerMapRevision {
  id: Id;
  tenantId: Id;
  questionId: Id;
  normalizedAnswer: string;
  previousOpinion: ResolvedOpinion | null;
  previousSource: OpinionSource | null;
  newOpinion: ResolvedOpinion;
  newSource: OpinionSource;
  changedByActorType: string;
  changedByActorId: string;
  changedAt: Date;
  reason: string | null;
}

// ─── Question rubric (§14) ───────────────────────────────────────────────────

/**
 * Everything the evaluator and a reviewing facilitator need in order to judge an
 * answer, beyond the canonical answer string.
 *
 * Every field is optional in practice — a question with only a canonical answer
 * still evaluates, just with less to go on.
 */
export interface QuestionRubric {
  canonicalAnswer: string;
  acceptedAnswers: string[];
  /** Concepts an answer must convey to be Correct. */
  requiredConcepts: string[];
  /** Concepts that strengthen an answer but are not needed for Correct. */
  optionalConcepts: string[];
  /** Domain terms treated as equivalent to rubric wording. */
  acceptedTerminology: string[];
  /** Abbreviation to expansion, applied during normalization for this question. */
  acceptableAbbreviations: Record<string, string>;
  correctExamples: string[];
  partialExamples: string[];
  incorrectExamples: string[];
  knownMisconceptions: string[];
  /** Stating one of these makes an answer Incorrect regardless of everything else. */
  disqualifyingConcepts: string[];
  /** Prose describing what a complete answer looks like. */
  completeAnswerDescription: string | null;
  explanation: string | null;
}

export const EMPTY_RUBRIC: QuestionRubric = {
  canonicalAnswer: "",
  acceptedAnswers: [],
  requiredConcepts: [],
  optionalConcepts: [],
  acceptedTerminology: [],
  acceptableAbbreviations: {},
  correctExamples: [],
  partialExamples: [],
  incorrectExamples: [],
  knownMisconceptions: [],
  disqualifyingConcepts: [],
  completeAnswerDescription: null,
  explanation: null,
};

// ─── Configuration (§4, §5, §7) ──────────────────────────────────────────────

/**
 * Thresholds for the conservative parts of the engine.
 *
 * The fuzzy numbers are deliberately severe. Fuzzy matching exists to absorb
 * misspellings and inflections, not to guess meaning; every threshold here is
 * set so that the failure mode is "escalate to semantic evaluation", which costs
 * a fraction of a cent, rather than "guess", which costs a wrong ruling.
 */
export interface EvaluationConfig {
  /** Similarity at or above which a fuzzy variation may inherit an opinion. */
  fuzzyAcceptThreshold: number;
  /**
   * A fuzzy match is rejected unless the best-scoring entry beats the best
   * entry holding a *different* opinion by at least this margin. "Duty of Care"
   * and "Duty of Loyalty" must never resolve each other.
   */
  fuzzyAmbiguityMargin: number;
  /** Answers shorter than this many non-space characters never fuzzy-match. */
  fuzzyMinChars: number;
  /** Per-token similarity floor; one badly-mismatched word voids the whole match. */
  fuzzyPerTokenFloor: number;
  /** Below this confidence, a semantic verdict is downgraded to Unknown. */
  aiConfidenceFloor: number;
  /** A machine-learned entry may be promoted to trusted at or above this confidence… */
  trustConfidenceThreshold: number;
  /** …and only after being seen at least this many times with no human override. */
  trustRepeatThreshold: number;
  /** Original spellings retained per Answer Map entry. */
  maxStoredExamples: number;
  /** Hard timeout for one semantic evaluation call. */
  semanticTimeoutMs: number;
  /** Unique answers sent in a single semantic evaluation request. */
  semanticBatchSize: number;
  /**
   * Whether the engine may call a semantic provider.
   *
   * The engine's own switch. Deployments set it from the tenant's AI-grading
   * permission and the facilitator's per-event toggle rather than configuring it
   * directly, so there is one answer to "is AI grading on" rather than two.
   */
  semanticEnabled: boolean;
  /** Answers pre-generated per bucket during Answer Map enrichment. */
  enrichmentTargetPerBucket: number;
}

export const DEFAULT_EVALUATION_CONFIG: EvaluationConfig = {
  // One transposed letter in a fourteen-character word scores ~0.93; one wrong
  // letter in a six-character word scores ~0.83 and is left for the AI.
  fuzzyAcceptThreshold: 0.92,
  fuzzyAmbiguityMargin: 0.08,
  fuzzyMinChars: 8,
  fuzzyPerTokenFloor: 0.6,
  aiConfidenceFloor: 0.6,
  trustConfidenceThreshold: 0.9,
  trustRepeatThreshold: 3,
  maxStoredExamples: 5,
  semanticTimeoutMs: 4000,
  semanticBatchSize: 25,
  semanticEnabled: false,
  enrichmentTargetPerBucket: 8,
};
